import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, ApiError } from "../api/client";
import type { Organization } from "../api/types";
import { ErrorState, LoadingState } from "../components/AsyncState";
import { ListToolbar, Metric, Modal, WorkflowEmpty } from "../components/WorkflowUI";
import { roleLabel } from "../utils/labels";

export function OrganizationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("created-desc");
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setItems(await apiClient.listOrganizations()); setError(""); }
    catch (reason) { setError(reason instanceof ApiError ? reason.message : "组织列表加载失败"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const visible = useMemo(() => filterOrganizations(items, query, sort), [items, query, sort]);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true); setError("");
    try {
      const organization = await apiClient.createOrganization(String(data.get("slug")), String(data.get("name")));
      window.sessionStorage.setItem("active-organization", organization.id);
      setCreating(false);
      navigate(`/organizations/${organization.id}/projects`);
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "创建组织失败"); }
    finally { setBusy(false); }
  }

  return <>
    <header className="page-header dashboard-header"><div><p className="eyebrow">组织管理</p><h1>组织与项目</h1><p>选择工作空间，然后按项目、任务和工作管理标注流程。</p></div><button className="button primary" type="button" onClick={() => setCreating(true)}>+ 新建组织</button></header>
    <section className="metric-strip" aria-label="组织概览"><Metric label="我的组织" value={items.length} /><Metric label="可管理" value={items.filter((item) => item.role === "owner" || item.role === "maintainer").length} /><Metric label="参与协作" value={items.filter((item) => item.role === "worker" || item.role === "supervisor").length} /></section>
    <section className="resource-section card">
      <div className="section-heading"><div><h2>组织</h2><p>项目、成员和权限都归属于组织。</p></div></div>
      <ListToolbar query={query} onQueryChange={setQuery} placeholder="按组织名称或标识搜索" count={visible.length} sort={sort} onSortChange={setSort} />
      {loading ? <LoadingState /> : error && !creating ? <ErrorState message={error} retry={() => void load()} /> : visible.length === 0 ? <WorkflowEmpty title={items.length ? "没有匹配的组织" : "还没有组织"} detail={items.length ? "请修改搜索条件。" : "创建组织后，即可邀请成员并建立项目。"} action={!items.length ? <button className="button primary" onClick={() => setCreating(true)}>新建组织</button> : undefined} /> : <div className="resource-grid">{visible.map((item) => <Link key={item.id} className="resource-card" to={`/organizations/${item.id}/projects`} onClick={() => window.sessionStorage.setItem("active-organization", item.id)}>
        <div className="resource-card-icon" aria-hidden="true">组</div><div className="resource-card-main"><strong>{item.name}</strong><small>{item.slug}</small><p>进入组织查看项目、成员与邀请</p></div><span className="role-chip">{roleLabel(item.role)}</span><span className="resource-arrow" aria-hidden="true">→</span>
      </Link>)}</div>}
    </section>
    <Modal title="新建组织" open={creating} onClose={() => setCreating(false)}>
      <form className="modal-form" onSubmit={(event) => void create(event)}>
        <p className="form-intro">组织是项目与成员权限的边界。创建后你将成为所有者。</p>
        <label>组织名称<input name="name" autoFocus required placeholder="例如：知能清淤标注团队" /></label>
        <label>组织标识（Slug）<input name="slug" pattern="[a-z0-9][a-z0-9\-]{1,62}" required placeholder="例如：znqy-annotation" /><small>仅使用小写字母、数字和连字符，创建后用于稳定识别组织。</small></label>
        {error && <p className="inline-error" role="alert">{error}</p>}
        <footer className="modal-actions"><button className="button ghost" type="button" onClick={() => setCreating(false)}>取消</button><button className="button primary" disabled={busy}>{busy ? "正在创建…" : "创建并进入"}</button></footer>
      </form>
    </Modal>
  </>;
}

export function filterOrganizations(items: Organization[], query: string, sort: string): Organization[] {
  const normalized = query.trim().toLowerCase();
  return items.filter((item) => !normalized || `${item.name} ${item.slug}`.toLowerCase().includes(normalized)).sort((left, right) => {
    if (sort === "name-asc") return left.name.localeCompare(right.name, "zh-CN");
    if (sort === "name-desc") return right.name.localeCompare(left.name, "zh-CN");
    return String(right.created_at).localeCompare(String(left.created_at));
  });
}
